import { useState, type VoidFunctionComponent } from 'react';

import {
  commonLinuxArchs,
  FETCHER_DROPDOWN_OPTIONS,
  isLinuxFetcher,
  linuxDistrosByFetcher,
  type ThirdPartyFetcher,
} from '../../../../shared/fetchers';
import { selectOptionFactory } from '../../../utils';
import { FormLabelVariant, LabeledSelect, LabeledTextInput } from '../../generic';
import Button, { LoaderButton } from '../../generic/Button';
import type { ManuallyAddedDependencyRequest } from '../../../../typings/api';

import styles from './SearchDependency.module.less';

const COMMON_LINUX_ARCH_OPTIONS = commonLinuxArchs.map((linuxArch) => selectOptionFactory(linuxArch));

export type Props = {
  /**
   * Whether the dependency is being added as a vendored dependency.
   */
  isVendored?: boolean;

  /**
   * Callback to save the dependency.
   * NOTE: The return value here is typed as "unknown" because React Query's mutateAsync
   * returns whatever the mutation function returns but we don't care about it.
   */
  saveDependency?: (params: ManuallyAddedDependencyRequest) => Promise<unknown>;
  /**
   * Whether the dependency is currently being saved.
   */
  isSaving?: boolean;

  /**
   * Callback when the drawer is closed.
   */
  onClose?: () => void;
};

const SearchDependency: VoidFunctionComponent<Props> = ({
  isVendored,
  saveDependency,
  isSaving,
  onClose,
}) => {
  const [filePath, setFilePath] = useState<string>('');
  const [packageManager, setPackageManager] = useState<ThirdPartyFetcher | undefined>(undefined);
  const [name, setName] = useState<string>('');
  const [version, setVersion] = useState<string>('');

  const [distro, setDistro] = useState<string | undefined>(undefined);
  const [release, setRelease] = useState<string>('');
  const [arch, setArch] = useState<string | undefined>(undefined);

  const isSearchingForLinuxPackage = packageManager && isLinuxFetcher(packageManager);

  const canSaveVendored = isVendored ? !!filePath : true;
  const canSaveLinux = isSearchingForLinuxPackage ? !!distro && !!release && !!arch : true;
  const canSave = !!packageManager && !!name && !!version && canSaveVendored && canSaveLinux;

  const onSave = async () => {
    // NOTE: This will only ever be called if canSave is true but TS doesn't know that.
    if (!canSave) {
      return;
    }

    await saveDependency?.({
      // TODO (CORE-6486): Support overwriting manual deps (i.e., 'replace' kind, add an "existingDependencyLocator" prop?).
      kind: 'new',
      newDependency: {
        kind: 'locator',
        fetcher: packageManager,
        packageName: name,
        revision: version,
        vendoredPath: filePath || undefined,
        distro: distro || undefined,
        distroRelease: release || undefined,
        arch: arch || undefined,
      },
    });

    onClose?.();
  };

  return (
    <section>
      <header>
        <p className={styles.overview}>
          Search for an existing package and add it as a {isVendored ? 'vendored' : 'manual'} dependency to your project.
        </p>
        <h3 className={styles.title}>
          Package Details
        </h3>
      </header>

      <main className={styles.main}>
        {isVendored && (
          <div className={styles.field}>
            <LabeledTextInput
              id="search-dependency-file-path"
              size="small"
              label="File Path"
              labelVariant={FormLabelVariant.Filter}
              placeholder="Path to the vendored dependency"
              value={filePath}
              onChange={(value) => setFilePath(value)}
              isRequired
            />
            <p className={styles.notes}>Add the path to the vendored dependency.</p>
          </div>
        )}

        <div className={styles.field}>
          <LabeledSelect
            id="search-dependency-package-manager"
            label="Package Manager"
            variant={FormLabelVariant.Filter}
            placeholder="Select package manager..."
            options={FETCHER_DROPDOWN_OPTIONS}
            value={FETCHER_DROPDOWN_OPTIONS.find((opt) => opt.value === packageManager)}
            onChange={(opt) => setPackageManager(opt?.value)}
            isRequired
          />
          <p className={styles.notes}>This tells FOSSA where to grab the same package for our analysis.</p>
        </div>

        <div className={styles.field}>
          <LabeledTextInput
            id="search-dependency-name"
            size="small"
            label="Name"
            labelVariant={FormLabelVariant.Filter}
            placeholder="Enter a package name"
            value={name}
            onChange={(value) => setName(value)}
            isRequired
          />
        </div>

        <div className={styles.field}>
          <LabeledTextInput
            id="search-dependency-version"
            size="small"
            label="Version"
            labelVariant={FormLabelVariant.Filter}
            placeholder="Enter a version number, eg. v1 or 1.0"
            value={version}
            onChange={(value) => setVersion(value)}
            isRequired
          />
        </div>
        {isSearchingForLinuxPackage && (
          <div className={styles.linuxFields}>
            <LabeledSelect
              id="search-dependency-distro"
              label="Distro"
              variant={FormLabelVariant.Filter}
              placeholder="Select distribution..."
              options={linuxDistrosByFetcher[packageManager]}
              value={linuxDistrosByFetcher[packageManager]?.find((opt) => opt.value === distro)}
              onChange={(opt) => setDistro(opt?.value)}
              isRequired
            />
            <LabeledTextInput
              id="search-dependency-release"
              size="small"
              label="Release"
              labelVariant={FormLabelVariant.Filter}
              placeholder="24.04"
              value={release}
              onChange={(value) => setRelease(value)}
              isRequired
            />
            <LabeledSelect
              id="search-dependency-arch"
              label="Architecture"
              variant={FormLabelVariant.Filter}
              placeholder="Select architecture..."
              options={COMMON_LINUX_ARCH_OPTIONS}
              value={COMMON_LINUX_ARCH_OPTIONS.find((opt) => opt.value === arch)}
              onChange={(opt) => setArch(opt?.value)}
              isRequired
            />
          </div>
        )}
      </main>

      <footer className={styles.footer}>
        <Button type="secondary" onClick={() => onClose?.()}>Cancel</Button>
        <LoaderButton
          type="primary"
          onClick={onSave}
          isDisabled={!canSave}
          isLoading={isSaving}
        >
          Save
        </LoaderButton>
      </footer>
    </section>
  );
};

export default SearchDependency;
